import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function Auth({ mode = "login" }) {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const isLogin = mode === "login";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      let u;
      if (isLogin) u = await login(form.email, form.password);
      else u = await register({ name: form.name, email: form.email, password: form.password, phone: form.phone });
      toast.success(isLogin ? "Welcome back!" : "Account created!");
      if (u.role === "vendor") navigate("/vendor");
      else if (u.role === "admin") navigate("/admin");
      else navigate("/account");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mx-auto grid min-h-[70vh] max-w-6xl grid-cols-1 lg:grid-cols-2 gap-0 px-4 py-10">
      <div className="hidden lg:block relative overflow-hidden rounded-l-3xl">
        <img src="https://images.unsplash.com/photo-1758448500717-2e4bcd79108b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-midnight/70" />
        <div className="relative p-12 flex flex-col justify-end h-full">
          <div className="rounded-2xl bg-white/95 px-5 py-3 w-fit"><Logo className="h-10 w-auto" /></div>
          <p className="mt-3 text-lg text-white/80">Everything for Everyday Living. Sign in to shop across all four marts, track orders and save your favourites.</p>
        </div>
      </div>

      <div className="flex flex-col justify-center rounded-3xl lg:rounded-l-none border border-border p-8 md:p-12">
        <h1 className="text-3xl font-extrabold text-midnight">{isLogin ? "Welcome back" : "Create your account"}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{isLogin ? "Sign in to continue" : "Join KRVMart in seconds"}</p>

        {err && <div className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="auth-error">{err}</div>}

        <form onSubmit={submit} className="mt-6 space-y-3">
          {!isLogin && <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" data-testid="auth-name" className="w-full rounded-xl border border-border px-4 py-3 text-sm outline-none focus:border-gold" />}
          <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email address" data-testid="auth-email" className="w-full rounded-xl border border-border px-4 py-3 text-sm outline-none focus:border-gold" />
          {!isLogin && <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone (optional)" data-testid="auth-phone" className="w-full rounded-xl border border-border px-4 py-3 text-sm outline-none focus:border-gold" />}
          <input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Password" data-testid="auth-password" className="w-full rounded-xl border border-border px-4 py-3 text-sm outline-none focus:border-gold" />
          <button disabled={busy} data-testid="auth-submit" className="w-full rounded-full bg-midnight py-3.5 font-bold text-white disabled:opacity-60">{busy ? "Please wait…" : isLogin ? "Login" : "Create Account"}</button>
        </form>

        <div className="my-5 flex items-center gap-3"><span className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">OR</span><span className="h-px flex-1 bg-border" /></div>

        <button onClick={google} data-testid="google-login-btn" className="flex w-full items-center justify-center gap-3 rounded-full border border-border py-3.5 font-semibold hover:bg-secondary transition-colors">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" /> Continue with Google
        </button>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {isLogin ? "New to KRVMart? " : "Already have an account? "}
          <Link to={isLogin ? "/register" : "/login"} className="font-bold text-gold-dark">{isLogin ? "Create account" : "Login"}</Link>
        </p>
      </div>
    </div>
  );
}
